import React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuSub,
  MenuSubContent,
  MenuSubTrigger,
  MenuTrigger,
} from "@replo/design-system/components/menu/Menu";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { useAdminPanelActions } from "@v2/features/admin/adminPanelStore";
import { useResetFiles } from "@v2/features/debug/useResetFiles";
import { NextDevServerContext } from "@v2/features/page-renderer/components/code-execution/context";
import { useDynamicCommandMenuItem } from "@v2/hooks/useDynamicCommandMenuItems";
import { useIsDebugMode } from "@v2/hooks/useIsDebugMode";
import { useLocalStorageState } from "@v2/hooks/useLocalStorage";
import shouldShowV2Debuggers from "@v2/utils/shouldShowV2Debuggers";
import {
  Bug,
  Copy,
  ExternalLink,
  FileText,
  MessageSquare,
  RefreshCcw,
  ScrollText,
  Wrench,
  XOctagon,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { z } from "zod";

export const DebugMenu = () => {
  const [isResetFilesConfirmModalOpen, setIsResetFilesConfirmModalOpen] =
    React.useState(false);
  const { open } = useAdminPanelActions();
  const [searchParams] = useSearchParams();

  const [showLogs, setShowLogs] = useLocalStorageState(
    "replo.debug.showNextDevServerLogs",
    false,
    { schema: z.boolean() },
  );

  const nextDevServerContext = React.useContext(NextDevServerContext);
  const sandboxUrl = nextDevServerContext?.url;

  const { resetFiles, isLoadingFilesReset } = useResetFiles();
  const shouldShowDebugOptions = shouldShowV2Debuggers();

  const chatId = searchParams.get("chatId");
  const fileId = searchParams.get("pageId");

  const isDebugMode = useIsDebugMode();

  // Open Chat in Admin (cmd-k menu item)
  const openChatInAdminItem = React.useMemo(() => {
    if (!isDebugMode || !chatId) {
      return null;
    }

    return {
      label: "Open Chat in Admin",
      group: "debug" as const,
      icon: { type: "iconComponent" as const, component: ExternalLink },
      onSelect: () => {
        window.open(
          `https://admin.replo.app/dashboard/chats?chatId=${chatId}`,
          "_blank",
        );
      },
    };
  }, [isDebugMode, chatId]);
  useDynamicCommandMenuItem("debug-open-chat-admin", openChatInAdminItem);

  // Copy Chat ID (cmd-k menu item)
  const copyChatIdItem = React.useMemo(() => {
    if (!isDebugMode || !chatId) {
      return null;
    }

    return {
      label: "Copy Chat ID",
      group: "debug" as const,
      icon: { type: "iconComponent" as const, component: Copy },
      onSelect: () => {
        void navigator.clipboard.writeText(chatId);
      },
    };
  }, [isDebugMode, chatId]);
  useDynamicCommandMenuItem("debug-copy-chat-id", copyChatIdItem);

  // Open File in Admin (cmd-k menu item)
  const openFileInAdminItem = React.useMemo(() => {
    if (!isDebugMode || !fileId) {
      return null;
    }

    return {
      label: "Open File in Admin",
      group: "debug" as const,
      icon: { type: "iconComponent" as const, component: ExternalLink },
      onSelect: () => {
        window.open(
          `https://admin.replo.app/dashboard/files?fileId=${fileId}`,
          "_blank",
        );
      },
    };
  }, [isDebugMode, fileId]);
  useDynamicCommandMenuItem("debug-open-file-admin", openFileInAdminItem);

  // Copy File ID (cmd-k menu item)
  const copyFileIdItem = React.useMemo(() => {
    if (!isDebugMode || !fileId) {
      return null;
    }

    return {
      label: "Copy File ID",
      group: "debug" as const,
      icon: { type: "iconComponent" as const, component: Copy },
      onSelect: () => {
        void navigator.clipboard.writeText(fileId);
      },
    };
  }, [isDebugMode, fileId]);
  useDynamicCommandMenuItem("debug-copy-file-id", copyFileIdItem);

  // Copy Preview URL (cmd-k menu item)
  const copyPreviewUrlItem = React.useMemo(() => {
    if (!isDebugMode || !sandboxUrl) {
      return null;
    }

    return {
      label: "Copy Preview URL",
      group: "debug" as const,
      icon: { type: "iconComponent" as const, component: Copy },
      onSelect: () => {
        void navigator.clipboard.writeText(sandboxUrl);
      },
    };
  }, [isDebugMode, sandboxUrl]);
  useDynamicCommandMenuItem("debug-copy-preview-url", copyPreviewUrlItem);

  if (!shouldShowDebugOptions) {
    return null;
  }

  const copyToClipboard = (text: string) => {
    void navigator.clipboard.writeText(text);
  };

  return (
    <>
      <Menu>
        <Tooltip content="Debug Options">
          <MenuTrigger
            render={
              <Button icon variant="tertiary" size="base">
                <Bug />
              </Button>
            }
          />
        </Tooltip>
        <MenuContent align="start" side="bottom" UNSAFE_className="w-[200px]">
          <MenuItem
            label="Open Debug Panel"
            startEnhancer={<Wrench />}
            onClick={() => {
              open();
            }}
          />
          <MenuItem
            label="Refresh"
            startEnhancer={<RefreshCcw />}
            onClick={() => {
              // reboot();
            }}
          />
          <MenuItem
            label={
              showLogs
                ? "Hide Next Dev Server Logs"
                : "Show Next Dev Server Logs"
            }
            startEnhancer={<ScrollText />}
            onClick={() => {
              setShowLogs(!showLogs);
            }}
          />
          <MenuItem
            label="Copy Preview URL"
            startEnhancer={<Copy />}
            onClick={() => {
              copyToClipboard(sandboxUrl ?? "");
            }}
          />
          {chatId && (
            <MenuSub>
              <MenuSubTrigger label="Chat" startEnhancer={<MessageSquare />} />
              <MenuSubContent>
                <MenuItem
                  label="Copy ID"
                  startEnhancer={<Copy />}
                  onClick={() => {
                    copyToClipboard(chatId);
                  }}
                />
                <MenuItem
                  label="View in Admin"
                  startEnhancer={<ExternalLink />}
                  onClick={() => {
                    window.open(
                      `https://admin.replo.app/dashboard/chats?chatId=${chatId}`,
                      "_blank",
                    );
                  }}
                />
              </MenuSubContent>
            </MenuSub>
          )}
          {fileId && (
            <MenuSub>
              <MenuSubTrigger label="File" startEnhancer={<FileText />} />
              <MenuSubContent>
                <MenuItem
                  label="Copy ID"
                  startEnhancer={<Copy />}
                  onClick={() => {
                    copyToClipboard(fileId);
                  }}
                />
                <MenuItem
                  label="View in Admin"
                  startEnhancer={<ExternalLink />}
                  onClick={() => {
                    window.open(
                      `https://admin.replo.app/dashboard/files?fileId=${fileId}`,
                      "_blank",
                    );
                  }}
                />
              </MenuSubContent>
            </MenuSub>
          )}
          <MenuSeparator />
          <MenuItem
            label="Reset Files"
            variant="destructive"
            startEnhancer={<XOctagon className="text-rose" />}
            onClick={() => {
              setIsResetFilesConfirmModalOpen(true);
            }}
          />
        </MenuContent>
      </Menu>
      <Modal
        open={isResetFilesConfirmModalOpen}
        onOpenChange={setIsResetFilesConfirmModalOpen}
        title="Reset Files"
        description="This action will reset all files in the project and remove all funnels. DATA WILL BE LOST. This is not a soft-delete which can be recovered, do not click this unless you really know what you're doing and are okay with losing existing pages and funnels in this project FOREVER."
        footer={
          <div className="flex gap-2 justify-end">
            <Button
              variant="secondary"
              onClick={() => setIsResetFilesConfirmModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void resetFiles()}
              loading={isLoadingFilesReset}
            >
              Reset Files
            </Button>
          </div>
        }
      />
    </>
  );
};
